import { defineStore } from 'pinia'

import {
    attachTrafficSourceToPool,
    detachTrafficSourceFromPool,
    getAvailableTrafficSources,
    updateTrafficSourcePool,
} from '@/api/pool-routes'

import { getFilterOnPageInLocalStorageByWorkspaceId, setFilterOnPageInLocalStorageByWorkspaceId } from '@/helpers/save-sorting-local-storage-helper'

import { useDefaultStore } from '@/store/defaultStore'
import { useDetailNumberPoolStore } from '@/store/call-tracking/numberPools/detailNumberPoolStore'
import { usePermissionsStore } from '@/store/permissionsStore'
import { useLocalFilterTableStore } from '@/store/localFilterTableStore'

import { FILTER_NAME } from '@/constants/localFilters'
import { SETTINGS_HEADER_TABLE } from '@/constants/headersTable/settingsHeaderTable'
import { DETAIL_NUMBER_POOL_TRAFFIC_SOURCES_HEADERS_TABLE, DETAIL_NUMBER_POOL_TRAFFIC_SOURCES_HEADERS_TABLE_WITH_ACTIONS } from '@/constants/headersTable/detailNumberPoolTrafficSourcesHeadersTable'

export const useDetailNumberPoolTrafficSourcesStore = defineStore('detailNumberPoolTrafficSourcesStore', {
    state: () => ({
        loading: false,
        actionLoading: false,

        onPage: 25,
        onPageAttachTrafficSources: 25,

        id: null,
        availableTrafficSources: [],
        trafficSources: [],
        googleConversions: [],
        facebookEvents: [],

        availableTrafficSourcesFilters: [FILTER_NAME],
        trafficSourcesFilters: [
            FILTER_NAME,
        ],

        search: null,
    }),

    getters: {
        headersByPermissions() {
            const permissionsStore = usePermissionsStore()

            return (permissionsStore.permissions.numberPools.edit || permissionsStore.permissions.trafficSources.view)
                ? DETAIL_NUMBER_POOL_TRAFFIC_SOURCES_HEADERS_TABLE_WITH_ACTIONS.map((head, index) => ({
                    ...head,
                    orderIndex: index,
                }))
                : DETAIL_NUMBER_POOL_TRAFFIC_SOURCES_HEADERS_TABLE.map((head, index) => ({
                    ...head,
                    orderIndex: index,
                }))
        },
    },

    actions: {
        changeOnPage(onPage) {
            this.onPage = onPage

            setFilterOnPageInLocalStorageByWorkspaceId(onPage, `${SETTINGS_HEADER_TABLE.numberPoolTrafficSources}.${this.id}`)
        },

        getLocalStorageOnPage() {
            const savedOnPage = getFilterOnPageInLocalStorageByWorkspaceId(`${SETTINGS_HEADER_TABLE.numberPoolTrafficSources}.${this.id}`)

            this.onPage = savedOnPage ? Number(savedOnPage) : 25
        },

        changeOnPageAttachTrafficSource(onPage) {
            this.onPageAttachTrafficSources = onPage

            setFilterOnPageInLocalStorageByWorkspaceId(onPage, `${SETTINGS_HEADER_TABLE.numberPoolAttachTrafficSources}.${this.id}`)
        },

        getLocalStorageOnPageAttachTrafficSources() {
            const savedOnPage = getFilterOnPageInLocalStorageByWorkspaceId(`${SETTINGS_HEADER_TABLE.numberPoolAttachTrafficSources}.${this.id}`)

            this.onPageAttachTrafficSources = savedOnPage ? Number(savedOnPage) : 25
        },

        setData(payload, id) {
            this.id = id
            this.trafficSources = payload.traffic_sources
            this.googleConversions = payload.google_ads_conversions && Array.isArray(payload.google_ads_conversions) ? payload.google_ads_conversions : []
            this.facebookEvents = payload.facebook_ads_events && Array.isArray(payload.facebook_ads_events) ? payload.facebook_ads_events : []
        },

        onSearch(search) {
            this.search = search
        },

        getQueryLocalStorage() {
            const localFilterTableStore = useLocalFilterTableStore()
            localFilterTableStore.initializeTableSettings(SETTINGS_HEADER_TABLE.detailNumberPoolAvailableTrafficSources, this.availableTrafficSourcesFilters)
            localFilterTableStore.getQueryLocalStorage(SETTINGS_HEADER_TABLE.detailNumberPoolAvailableTrafficSources, this.id)
            localFilterTableStore.initializeTableSettings(SETTINGS_HEADER_TABLE.detailNumberPoolTrafficSources, this.trafficSourcesFilters)
            localFilterTableStore.setHeaders(SETTINGS_HEADER_TABLE.detailNumberPoolTrafficSources, this.headersByPermissions)
            localFilterTableStore.getQueryLocalStorage(SETTINGS_HEADER_TABLE.detailNumberPoolTrafficSources, this.id)
            this.getLocalStorageOnPage()
            this.getLocalStorageOnPageAttachTrafficSources()
        },

        async goToLoadAvailableTrafficSources(withoutLoading = false) {
            const defaultStore = useDefaultStore()
            const detailNumberPoolsStore = useDetailNumberPoolStore()

            this.loading = !withoutLoading

            const { success, payload, message } = await getAvailableTrafficSources(detailNumberPoolsStore.slug)

            if (success) {
                this.availableTrafficSources = payload
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.loading = false
        },

        async goToAttachTrafficSourceToPool(formData) {
            const defaultStore = useDefaultStore()
            const detailNumberPoolsStore = useDetailNumberPoolStore()
            this.actionLoading = true

            const { success, payload, message } = await attachTrafficSourceToPool(detailNumberPoolsStore.slug, formData)

            if (success) {
                this.trafficSources = payload.traffic_sources
                this.googleConversions = payload.google_ads_conversions
                this.facebookEvents = payload.facebook_ads_events
                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return { success }
        },

        async goToDetachTrafficSource({ slug, traffic_sources }) {
            const defaultStore = useDefaultStore()
            this.actionLoading = true

            const { success, message } = await detachTrafficSourceFromPool(slug, traffic_sources)

            if (success) {
                this._removeTrafficSourcesAfterDetach(traffic_sources)
                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return { success }
        },

        async goToUpdateTrafficSource({ slug, formData, type }) {
            const defaultStore = useDefaultStore()
            this.actionLoading = true

            const { success, payload, message } = await updateTrafficSourcePool(slug, formData)

            if (success) {
                this.googleConversions = payload.google_ads_conversions
                this.facebookEvents = payload.facebook_ads_events

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return { success }
        },

        _removeTrafficSourcesAfterDetach(slugs) {
            this.trafficSources = this.trafficSources.filter((trafficSource) => !slugs.includes(trafficSource.slug))
        },
    },
})
